import { useList } from '@/voc/use/use-list.js';
import { ref } from 'vue';
import { useVocItemsTableStore } from '@/stores/voc-items-table.store';

import { v4 as uuid } from 'uuid';

import { isCancelledError, runAggregationOnce, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { hashCode } from '@/utils/string.utils';
import { EXPLORE_STATUSES, getExploreStatus } from '@/voc/helpers/permissions.helper';

import VocItemsAggregation from '@/voc/aggregations/voc-items/voc-items.aggregation';
import VocItemCountsAggregation from '@/voc/aggregations/voc-item-counts/voc-item-counts.aggregation.js';

import { useSubscriptionStore } from '@/stores/subscription.store';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import { useVocItemsJiraStore } from '@/stores/voc-items-jira.store';

const promiseStore = new Map();
const fetchingVocItems = {
    idea: ref(false),
    feedback: ref(false),
};

const inFlightVocItemFetches = {
    idea: 0,
    feedback: 0,
};

export function useVocItems ({ type = 'idea' } = {}) {
    type ??= 'idea';
    const aggregationId = `listen-voc-items-${type}-${uuid()}`;
    const aggregationCountId = `listen-voc-items-count-${type}-${uuid()}`;

    const {
        item: getVocItemById,
        items: vocItems,
        addItem: addVocItem,
        addItems: addVocItems,
        clearList: clearVocItems,
        keepAlive: keepAliveVocItems,
        updateItem: updateVocItem,
        removeItem: removeVocItem,
        cleanup: cleanupVocItems,
    } = useList(`listen-voc-items-${type}`);

    async function fetchVocItems ({ filters, revenue = false, limit = 0, sortBy }) {
        const promiseKey = hashCode(`voc-items-${type}-${JSON.stringify(filters)}-${limit}-${sortBy}`);

        if (promiseStore.get(promiseKey)) return promiseStore.get(promiseKey);

        const { accountDisplayName, visitorDisplayName, revenueField, accountTypeField, jiraBaseUrl } =
            await loadCommonFields({ revenue });
        VocItemsAggregation.subProductArea = SUB_PRODUCT_AREAS.CAPTURE;

        inFlightVocItemFetches[type] += 1;
        fetchingVocItems[type].value = true;

        promiseStore.set(
            promiseKey,
            runAggregationOnce(
                VocItemsAggregation,
                {
                    ...filters,
                    revenueField,
                    type,
                    expandInsights: getExploreStatus() === EXPLORE_STATUSES.READY,
                    limit,
                    sortBy,
                    accountDisplayName,
                    visitorDisplayName,
                    accountTypeField,
                    ...(jiraBaseUrl && { jiraBaseUrl }),
                },
                aggregationId,
            )
                .then((vocItems) => {
                    clearVocItems();
                    addVocItems(vocItems);
                })
                .catch((error) => {
                    if (!isCancelledError(error)) clearVocItems();

                    throw error;
                })
                .finally(() => {
                    promiseStore.delete(promiseKey);
                    inFlightVocItemFetches[type] -= 1;
                    if (!inFlightVocItemFetches[type]) fetchingVocItems[type].value = false;
                }),
        );

        return promiseStore.get(promiseKey);
    }

    async function fetchVocItemsCounts ({ filters, revenue = false }) {
        const promiseKey = hashCode(`voc-item-counts-${type}-${JSON.stringify(filters)}`);

        if (promiseStore.get(promiseKey)) return promiseStore.get(promiseKey);

        const { accountTypeField, jiraBaseUrl } = await loadCommonFields({ revenue });
        VocItemCountsAggregation.subProductArea = SUB_PRODUCT_AREAS.CAPTURE;

        const countPromise = runAggregationOnce(
            VocItemCountsAggregation,
            {
                filters: jiraBaseUrl ? { ...filters, jiraBaseUrl } : filters,
                type: 'feedback',
                accountTypeField,
                disableDatastoreIterator: true,
            },
            aggregationCountId,
        ).finally(() => {
            promiseStore.delete(promiseKey);
        });

        promiseStore.set(promiseKey, countPromise);

        return countPromise;
    }

    async function downloadCSV ({ filters, revenue = false, limit = 0, sortBy }) {
        const { accountDisplayName, visitorDisplayName, revenueField, accountTypeField, jiraBaseUrl } =
            await loadCommonFields({ revenue });

        VocItemsAggregation.subProductArea = SUB_PRODUCT_AREAS.CAPTURE;
        VocItemsAggregation.responseFormat = 'default';

        const columns = useVocItemsTableStore().columns
            ?.filter((column) => column.visible && column.downloadable !== false)
            ?.map((column) => ({
                field: column.csvProp || column.prop,
                label: column.csvLabel || column.label,
            }));

        return runAggregationOnce(
            VocItemsAggregation,
            {
                ...filters,
                revenueField,
                type,
                expandInsights: false,
                limit,
                sortBy,
                accountDisplayName,
                visitorDisplayName,
                accountTypeField,
                ...(jiraBaseUrl && { jiraBaseUrl }),
                isCsv: true,
                columns,
            },
            aggregationId,
        )
            .then((url) => {
                return url;
            })
            .catch((error) => {
                throw error;
            })
            .finally(() => {
                VocItemsAggregation.responseFormat = 'rows';
            });
    }

    async function loadCommonFields ({ revenue = false }) {
        const canUseVisitorDisplayName = hasSegmentFlag('visitorDisplayNameMapping');
        let accountDisplayName = useSubscriptionStore().settings.accountDisplayName?.value || null;
        let visitorDisplayName = canUseVisitorDisplayName
            ? useSubscriptionStore().settings.visitorDisplayName?.value || null
            : null;
        let revenueField = revenue ? useSubscriptionStore().settings.revenue?.value : null;
        let accountTypeField = useSubscriptionStore().settings.accountType?.value || null;
        const jiraStore = useVocItemsJiraStore();
        let jiraBaseUrl = jiraStore.serverInfo?.baseUrl || '';

        const prereqPromises = [];

        if (!jiraBaseUrl && jiraStore.shouldShowJiraIntegration) {
            prereqPromises.push(jiraStore.loadServerInfo().catch((error) => error));
        }

        if (revenue && !revenueField) {
            prereqPromises.push(useSubscriptionStore().loadSetting({ setting: 'revenue' }).catch((error) => error));
        }

        if (!accountDisplayName) {
            prereqPromises.push(
                useSubscriptionStore().loadSetting({ setting: 'accountDisplayName', retrieveMetadata: false }).catch(
                    (error) => error,
                ),
            );
        }

        if (canUseVisitorDisplayName && !visitorDisplayName) {
            prereqPromises.push(
                useSubscriptionStore().loadSetting({ setting: 'visitorDisplayName', retrieveMetadata: false }).catch(
                    (error) => error,
                ),
            );
        }

        if (!accountTypeField) {
            prereqPromises.push(
                useSubscriptionStore().loadSetting({ setting: 'accountType', retrieveMetadata: false }).catch(
                    (error) => error,
                ),
            );
        }

        if (prereqPromises.length) {
            await Promise.all(prereqPromises);

            accountDisplayName ??= useSubscriptionStore().settings.accountDisplayName?.value || null;
            if (canUseVisitorDisplayName) {
                visitorDisplayName ??= useSubscriptionStore().settings.visitorDisplayName?.value || null;
            }
            revenueField ??= revenue ? useSubscriptionStore().settings.revenue?.value : null;
            accountTypeField ??= useSubscriptionStore().settings.accountType?.value || null;
            jiraBaseUrl ||= jiraStore.serverInfo?.baseUrl || '';
        }

        return { accountDisplayName, visitorDisplayName, revenueField, accountTypeField, jiraBaseUrl };
    }

    return {
        fetchingVocItems: fetchingVocItems[type],
        fetchVocItemsCounts,
        vocItems,

        addVocItem,
        addVocItems,
        cleanupVocItems,
        clearVocItems,
        downloadCSV,
        fetchVocItems,
        getVocItemById,
        keepAliveVocItems,
        removeVocItem,
        updateVocItem,
    };
}
